<template>
	<div>
		<MaintenanceLayout v-if="isMaintenancePage">
			<MaintenanceView />
		</MaintenanceLayout>

		<AuthLayout v-else-if="isAuthRoute || currentUser === null">
			<div class="auth-card">
				<!-- Subtitle -->
				<p class="auth-subtitle">Sign in to continue to your dashboard</p>

				<!-- Auth Buttons -->
				<div class="auth-buttons">
					<!-- Hidden gapi button for functionality -->
					<div id="google-signin-button" style="display: none"></div>

					<!-- Custom styled Google button -->
					<button
						class="auth-button auth-button-google"
						@click="handleGoogleSignInClick"
					>
						<svg class="auth-button-icon" viewBox="0 0 24 24">
							<path
								fill="#4285F4"
								d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
							/>
							<path
								fill="#34A853"
								d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
							/>
							<path
								fill="#FBBC05"
								d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
							/>
							<path
								fill="#EA4335"
								d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
							/>
						</svg>
						<span>Continue with Google</span>
					</button>

					<!-- Microsoft Sign In Button -->
					<button
						class="auth-button auth-button-microsoft"
						@click="handleMicrosoftSignInClick"
					>
						<svg class="auth-button-icon" viewBox="0 0 24 24">
							<path fill="#f25022" d="M1 1h10v10H1z" />
							<path fill="#00a4ef" d="M1 13h10v10H1z" />
							<path fill="#7fba00" d="M13 1h10v10H13z" />
							<path fill="#ffb900" d="M13 13h10v10H13z" />
						</svg>
						<span>Continue with Microsoft</span>
					</button>
				</div>

				<!-- Terms -->
				<p class="auth-terms">
					By signing in, you agree to our
					<a href="https://www.talentics.app/privacy" target="_blank"
						>Privacy Policy</a
					>
				</p>

				<div v-if="isAuthRoute">
					<router-view></router-view>
				</div>
			</div>
		</AuthLayout>

		<DefaultLayout v-else>
			<router-view></router-view>
		</DefaultLayout>

		<CurrentUserPropDialog
			v-if="currentUser !== null && !isUserNameFilled && !isAuthRoute"
		></CurrentUserPropDialog>
		<EmailLinkSignInDialog
			v-if="emailLinkDialogVisible"
			:error-message="emailLinkSignInError"
			:loading="emailLinkSignInLoading"
			@submit="finishEmailLinkSignIn"
		></EmailLinkSignInDialog>
		<v-snackbar
			v-model="snackbar.isVisible"
			:timeout="snackbar.timeout"
			left
			bottom
		>
			{{ snackbar.text }}
			<template v-slot:action="{ attrs }">
				<v-btn
					dark
					text
					icon
					v-bind="attrs"
					@click="snackbar.isVisible = false"
					style="margin-right: 0.5rem"
				>
					<v-icon>close</v-icon>
				</v-btn>
			</template>
		</v-snackbar>
		<v-overlay :value="isLoading" :z-index="9999">
			<v-progress-circular indeterminate size="64"></v-progress-circular>
		</v-overlay>
		<v-snackbar
			:value="userRole === 900 || userRole === 999"
			:timeout="-1"
			color="pink"
			elevation="24"
			right
			bottom
		>
			<v-icon>support_agent</v-icon> Viewing as support agent
		</v-snackbar>
	</div>
</template>

<script>
import axios from 'axios';

import { mapState } from 'vuex';
import DefaultLayout from './components/layouts/defaultLayout/DefaultLayout';
import AuthLayout from './components/layouts/authLayout/AuthLayout';
import MaintenanceLayout from './components/layouts/maintenanceLayout/MaintenanceLayout';
import CurrentUserPropDialog from './components/shared/currentUserPropDialog/CurrentUserPropDialog';
import EmailLinkSignInDialog from './components/shared/emailLinkSignInDialog/EmailLinkSignInDialog';
import { auth } from './main';
import {
	getAuth,
	getIdToken,
	GoogleAuthProvider,
	isSignInWithEmailLink,
	signInWithEmailLink,
	OAuthProvider,
	signInWithCredential,
	signInWithPopup,
	onAuthStateChanged,
} from 'firebase/auth';
import MaintenanceView from './components/routes/maintenance/maintenanceView.vue';

export default {
	name: 'App',
	components: {
		DefaultLayout,
		AuthLayout,
		MaintenanceLayout,
		CurrentUserPropDialog,
		EmailLinkSignInDialog,
		MaintenanceView,
	},
	computed: {
		...mapState({
			currentUser: state => state.currentUser.currentUser,
			userRole: state => state.currentUser.userRole,
			currentLang: state => state.currentUser.lang,
			isLoading: state => state.apiTraffic.isLoading,
			isDark: state => state.currentUser.isDark,
		}),
		isUserNameFilled() {
			if (this.currentUser === null) {
				return true;
			}
			return Boolean(this.currentUser.name);
		},
	},
	data() {
		return {
			availableLangs: ['en', 'de', 'tr'],
			isMaintenancePage: false,
			isAuthRoute: false,
			snackbar: {
				isVisible: false,
				timeout: 3000,
				text: '',
			},
			emailLinkDialogVisible: false,
			emailLinkSignInError: null,
			emailLinkSignInLoading: false,
		};
	},
	methods: {
		parseJwt(token) {
			var base64Url = token.split('.')[1];
			var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
			var jsonPayload = decodeURIComponent(
				window
					.atob(base64)
					.split('')
					.map(function (c) {
						return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
					})
					.join(''),
			);

			return JSON.parse(jsonPayload);
		},
		handleGoogleSignInClick() {
			if (window.gapi && window.gapi.auth2) {
				const authInstance = window.gapi.auth2.getAuthInstance();
				if (authInstance) {
					authInstance
						.signIn()
						.then(user => {
							this.onGoogleSignIn(user);
						})
						.catch(error => {
							console.error('Google sign-in error:', error);
						});
				}
			}
		},
		onGoogleSignIn(user) {
			this.$store.commit('currentUser/setProvider', {
				provider: 'google.com',
			});
			this.$store.commit('currentUser/setGoogleUser', { user });

			const token = user.getAuthResponse(true);
			const { access_token } = token;
			const credential = GoogleAuthProvider.credential(null, access_token);

			signInWithCredential(auth, credential);
		},
		renderGoogleSignInButton() {
			if (window.gapi) {
				window.gapi.signin2.render('google-signin-button', {
					scope: 'email profile',
					response_type: 'id_token permission',
					onsuccess: this.onGoogleSignIn,
					theme: 'dark',
					longtitle: true,
					width: 340,
					height: 50,
				});
			} else {
				setTimeout(
					function () {
						this.renderGoogleSignInButton();
					}.bind(this),
					250,
				);
			}
		},
		completeEmailLinkSignIn() {
			const email = window.localStorage.getItem('emailForSignIn');

			// Same device/browser that requested the link: the email is cached,
			// so finish without bothering the user. Otherwise (the link was opened
			// elsewhere) Firebase still needs the email — which it deliberately does
			// not embed in the link — so ask for it via a dialog.
			if (email) {
				this.finishEmailLinkSignIn(email);
			} else {
				this.emailLinkDialogVisible = true;
			}
		},
		finishEmailLinkSignIn(email) {
			this.emailLinkSignInLoading = true;
			this.emailLinkSignInError = null;

			return signInWithEmailLink(auth, email, window.location.href)
				.then(() => {
					window.localStorage.removeItem('emailForSignIn');
					this.emailLinkDialogVisible = false;
				})
				.catch(error => {
					// eslint-disable-next-line no-console
					console.error('Email link sign-in failed', error);
					this.emailLinkSignInError = this.$t('label.sign-in-link-invalid');
					this.emailLinkDialogVisible = true;
				})
				.finally(() => {
					this.emailLinkSignInLoading = false;
				});
		},
		setLang({ lang = 'en', redirect = true }) {
			const { availableLangs, currentUser } = this;
			const paramsLang = this.$route.params.lang;

			if (lang && availableLangs?.indexOf(lang) > -1) {
				this.$i18n.locale = lang;

				if (!redirect) {
					return false;
				}

				if (!paramsLang || availableLangs?.indexOf(paramsLang) === -1) {
					const { query } = this.$route;
					this.$router.push({
						path: `/${lang}`,
						query,
					});

					return false;
				}

				if (paramsLang !== lang && currentUser) {
					const { path } = this.$route;
					const newPath = path.replace(`/${paramsLang}`, `/${lang}`);
					const { query } = this.$route;
					this.$router.push({
						path: newPath,
						query,
					});

					return false;
				}
			} else {
				const { query } = this.$route;
				this.$router.push({
					path: `/${lang}`,
					query,
				});

				return false;
			}
		},
		async handleMicrosoftSignInClick() {
			this.$store.commit('currentUser/setProvider', {
				provider: 'microsoft.com',
			});
			const provider = new OAuthProvider('microsoft.com');
			await signInWithPopup(getAuth(), provider);
		},
		setUserProvider(user) {
			const [provider] = user.providerData;
			const { providerId } = provider;
			this.$store.commit('currentUser/setProvider', {
				provider: providerId,
			});
		},
	},
	created() {
		axios.defaults.baseURL = process.env.VUE_APP_HOST_API_KEY;

		axios.interceptors.request.use(
			config => {
				if (config && config.talenticsSuppressGlobalLoading) {
					return config;
				}
				if (
					config &&
					// extract long-polling requests
					((config.url === '/v1/hire-app/user-approval-requests' &&
						config.method === 'get') ||
						// extract counter requests
						(config.url === '/v1/hire-app/applicants/counters' &&
							config.method === 'get') ||
						(config.url === '/v1/hire-app/applicants/counters/lead' &&
							config.method === 'get') ||
						(config.url === '/v1/hire-app/applicants/counters/interview' &&
							config.method === 'get') ||
						(config.url === '/v1/hire-app/applicants/counters/archive' &&
							config.method === 'get') ||
						// file viewer has its own in-dialog loader
						config.url.includes('/v1/hire-app/files/base64/'))
				) {
					return config;
				}
				this.$store.commit('apiTraffic/increaseRequests');
				return config;
			},
			() => {
				this.$store.commit('apiTraffic/increaseRequests');
			},
		);

		axios.interceptors.response.use(
			response => {
				if (response && response.config && response.config.talenticsSuppressGlobalLoading) {
					return response;
				}
				if (
					response &&
					response.config &&
					// extract long-polling request responses
					((response.config.url === '/v1/hire-app/user-approval-requests' &&
						response.config.method === 'get') ||
						// extract counter responses
						(response.config.url === '/v1/hire-app/applicants/counters' &&
							response.config.method === 'get') ||
						(response.config.url === '/v1/hire-app/applicants/counters/lead' &&
							response.config.method === 'get') ||
						(response.config.url ===
							'/v1/hire-app/applicants/counters/interview' &&
							response.config.method === 'get') ||
						(response.config.url ===
							'/v1/hire-app/applicants/counters/archive' &&
							response.config.method === 'get') ||
						// file viewer has its own in-dialog loader
						(response.config.url &&
							response.config.url.includes('/v1/hire-app/files/base64/')))
				) {
					return response;
				}
				this.$store.commit('apiTraffic/increaseResponses');
				if (
					!response.config.talenticsSuppressGlobalSnackbar &&
					(response.config.method === 'post' ||
						response.config.method === 'put' ||
						response.config.method === 'delete') &&
					response.config.url !== '/v1/hire-app/auth-user' &&
					response.config.url !== '/v1/hire-app/mentions/mark-as-seen' &&
					response.config.url !== '/v1/hire-app/notifications/mark-as-seen'
				) {
					const getText = ({ method }) => {
						if (method === 'post') {
							return this.$t('snackbar-messages.successfully-created');
						} else if (method === 'put') {
							return this.$t('snackbar-messages.successfully-updated');
						} else if (method === 'delete') {
							return this.$t('snackbar-messages.successfully-deleted');
						}
						return '';
					};
					this.snackbar = {
						isVisible: true,
						timeout: 3000,
						text: getText({ method: response.config.method }),
					};
				}
				// eslint-disable-next-line no-debugger
				return response;
			},
			error => {
				// extract long-polling request responses
				if (
					!(error && error.config && error.config.talenticsSuppressGlobalLoading) &&
					!(
						(error &&
							error.config &&
							error.config.url === '/v1/hire-app/user-approval-requests' &&
							error.config.method === 'get') ||
						(error.config.url === '/v1/hire-app/applicants/counters' &&
							error.config.method === 'get') ||
						(error.config.url === '/v1/hire-app/applicants/counters/lead' &&
							error.config.method === 'get') ||
						(error.config.url ===
							'/v1/hire-app/applicants/counters/interview' &&
							error.config.method === 'get') ||
						(error.config.url === '/v1/hire-app/applicants/counters/archive' &&
							error.config.method === 'get') ||
						(error.config.url &&
							error.config.url.includes('/v1/hire-app/files/base64/'))
					)
				) {
					this.$store.commit('apiTraffic/increaseResponses');
				}

				if (error && error.response) {
					if (
						error.response.data &&
						error.response.data.code === 'auth/id-token-expired'
					) {
						// REFRESH TOKEN
						const auth = getAuth();
						const { currentUser } = auth;
						return getIdToken(currentUser, true).then(idToken => {
							axios.defaults.headers.common['X-Auth-Token'] = idToken;
							error.config.headers['X-Auth-Token'] = idToken;
							return axios.request(error.config);
						});
					} else if (
						!(error.config && error.config.talenticsSuppressGlobalSnackbar) &&
						error.response.data &&
						error.response.data.code === 'AccessDenied'
					) {
						// Backend returns 403 { code: 'AccessDenied' } when the user is
						// not in the job's access list. Show a clear message instead of
						// the generic "an error occurred" fallback below.
						this.snackbar = {
							isVisible: true,
							timeout: 3000,
							text: this.$t('snackbar-messages.access-denied'),
						};
					} else if (
						!(error.config && error.config.talenticsSuppressGlobalSnackbar) &&
						error.response.data &&
						error.response.data.data &&
						error.response.data.data.message
					) {
						this.snackbar = {
							isVisible: true,
							timeout: 3000,
							text: error.response.data.data.message,
						};
					} else if (
						!(error.config && error.config.talenticsSuppressGlobalSnackbar) &&
						error.response.data
					) {
						this.snackbar = {
							isVisible: true,
							timeout: 3000,
							text:
								typeof error.response.data === 'object' &&
								error.response.data.message
									? this.$t(`snackbar-messages.${error.response.data.message}`)
									: this.$t('snackbar-messages.an-error-occurred'),
						};
					}
					return Promise.reject(error.response);
				}
				return Promise.reject(error);
			},
		);
	},
	mounted() {
		// Keep the gapi render for backwards compatibility if needed
		this.renderGoogleSignInButton();

		if (isSignInWithEmailLink(auth, window.location.href)) {
			this.completeEmailLinkSignIn();
		}

		onAuthStateChanged(auth, user => {
			if (user) {
				this.setUserProvider(user);
				user.getIdToken().then(idToken => {
					this.$store.dispatch('currentUser/authUser', { idToken }).then(() => {
						/**
						 * getIdToken() is called with true parameter to force
						 * refresh the current user's id token.
						 * This is required because auth-user endpoint
						 * creates custom claims for the id token received.
						 * This id token created with custom claims supersedes
						 * id token acquired above. When id token is refresh,
						 * however, it'll contain the necessary custom claims
						 * in it.
						 */
						user.getIdToken(true).then(refreshToken => {
							this.$store.commit('currentUser/setIdToken', {
								id_token: refreshToken,
							});
							const decodedTokenData = this.parseJwt(refreshToken);
							this.$store.dispatch('currentUser/setRole', {
								role: decodedTokenData.role,
							});
							this.$store.dispatch('company/get', {});
							this.$store.dispatch('pipelineInterview/get', {
								updateStore: true,
								refresh: true,
							});
							this.$store.dispatch('pipelineArchive/get', {
								updateStore: true,
								refresh: true,
							});
							this.$store.dispatch('pipelineLead/get', {
								updateStore: true,
								refresh: true,
							});
							this.$store.dispatch('userApprovalRequests/get', {
								updateStore: true,
								refresh: true,
							});
						});
					});
				});
			} else {
				setTimeout(
					function () {
						this.renderGoogleSignInButton();
					}.bind(this),
					500,
				);
			}
		});
	},
	watch: {
		currentLang: {
			handler: function (newVal, oldVal) {
				if (newVal !== oldVal) {
					const { fullPath } = this.$route;

					// TODO: Seperate this to a public layout
					const isMaintenancePage = fullPath?.includes('/maintenance');
					if (isMaintenancePage) {
						this.isMaintenancePage = isMaintenancePage;
					} else {
						// Prevent redirecting to main route if maintenance
						const isAuthRoute = fullPath?.indexOf('/auth/') > -1;
						this.isAuthRoute = isAuthRoute;
						this.setLang({ lang: newVal, redirect: !isAuthRoute });
					}
				}
			},
			deep: true,
			immediate: true,
		},
		isDark: {
			handler: function (newVal, oldVal) {
				if (newVal !== oldVal) {
					this.$vuetify.theme.dark = newVal;
				}
			},
			immediate: true,
		},
	},
};
</script>

<style lang="scss">
.page {
	max-width: 1185px;
	margin: 0 auto;
}
.theme--light.v-data-table.transparent-table {
	background: transparent;
}

.v-select {
	&.v-select--chips {
		&.v-select-talentics-chips {
			.v-select__selections {
				min-height: 42px !important;
			}
		}
	}
}

// Auth Card Styles
.auth-card {
	background: transparent;
	padding: 0;
}

.auth-subtitle {
	font-size: 1rem;
	color: #64748b;
	margin: 0 0 2rem 0;
	text-align: center;
}

.auth-buttons {
	display: flex;
	flex-direction: column;
	gap: 1rem;
}

.auth-button {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.75rem;
	padding: 1rem 1.5rem;
	border-radius: 8px;
	font-size: 0.9375rem;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s ease;
	border: none;
	font-family: inherit;

	&:hover {
		transform: translateY(-1px);
	}

	&:active {
		transform: translateY(0);
	}
}

.auth-button-icon {
	width: 20px;
	height: 20px;
	flex-shrink: 0;
}

.auth-button-google {
	background: #ffffff;
	border: 1px solid #e2e8f0;
	color: #475569;

	&:hover {
		border-color: #3b82f6;
		box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
	}
}

.auth-button-microsoft {
	background: #3b82f6;
	color: white;

	&:hover {
		background: #2563eb;
		box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
	}
}

.auth-terms {
	margin-top: 2rem;
	text-align: center;
	font-size: 0.8125rem;
	color: #94a3b8;
	line-height: 1.6;

	a {
		color: #3b82f6;
		text-decoration: none;

		&:hover {
			text-decoration: underline;
		}
	}
}

// Legacy styles kept for compatibility
fieldset.talentics-fieldset {
	border: 1px solid #9e9e9e;
	border-radius: 4px;
	margin-top: -10px;

	& > legend {
		color: rgba(0, 0, 0, 0.6);
		font-size: 12px;
		margin-left: 10px;
		padding-left: 4px;
		padding-right: 4px;
	}

	&.talentics-fieldset-paddings {
		padding: 0 0.5rem 0.5rem 0.5rem;

		& > legend {
			margin-left: 2px;
		}
	}

	&.talentics-fieldset-error {
		border: 2px solid #ff5252;

		& > legend {
			color: #ff5252;
		}
	}

	&.talentics-fieldset-disabled {
		border: 1px solid #bdbdbd;

		& > legend {
			color: #bdbdbd;
		}
	}

	& > * {
		border-radius: 4px;
	}
}

.theme--dark {
	fieldset.talentics-fieldset {
		legend {
			color: rgba(255, 255, 255, 0.7);
		}
	}

	.auth-card {
		background: #1e293b;
	}

	.auth-welcome-title {
		color: white;
	}

	.auth-welcome-subtitle {
		color: #94a3b8;
	}

	.auth-button-google {
		background: #334155;
		border-color: #475569;
		color: white;

		&:hover {
			border-color: #64748b;
		}
	}
}
</style>
